import { useContext, useState } from "react";

import { ArrowDropDown, Forum, Menu } from "@mui/icons-material";
import {
  AppBar as MuiAppBar,
  Badge,
  IconButton,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Grid,
} from "@mui/material";
import classNames from "classnames";
import Avatar from "react-avatar";
import { Link, useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import sweptLogo from "../../assets/images/swept.png";
import { APPBAR_HEIGHT, DRAWER_BREAKPOINT } from "../../constants";
import { handleSwitchCompany, UserContext } from "../../helpers";
import { drawerCollapse, drawerExpansion } from "../../theme";
import { AppbarPropsType, CompanyType } from "../../types";
import { getCompanyUnreadCounts } from "../messaging/helpers/unreadCounts";
import { useBadgePulse } from "../messaging/hooks/useBadgePulse";
import { badgePulseStyles } from "../messaging/styles/badgePulseStyles";
import AppBarDropdown from "./AppBarDropdown";

const useStyles = makeStyles()((theme) => ({
  appBar: {
    background: theme.palette.background.main,
    boxShadow: "none",
    zIndex: theme.zIndex.drawer + 1,
  },
  appBarMargin: {
    borderBottom: "none",
    height: APPBAR_HEIGHT,
    marginBottom: 0,
  },
  avatar: {
    fontWeight: "bold",
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
  },
  badge: {
    backgroundColor: theme.palette.error.dark,
    borderRadius: "4px",
    color: theme.palette.primary.contrastText,
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 700,
    lineHeight: 1,
    padding: "2px 5px",
    position: "absolute",
    top: "2px",
    left: "2px",
    right: "auto",
    width: "auto",
    height: "auto",
  },
  dropdown: {
    color: theme.palette.text.primary,
    backgroundColor: "#fff",
    boxShadow: [
      "0 2px 2px 0 rgba(0,0,0,0.14)",
      "0 1px 5px 0 rgba(0,0,0,0.12)",
      "0 3px 1px -2px rgba(0,0,0,0.2)",
    ].join(","),
    position: "absolute",
    top: `calc(${APPBAR_HEIGHT}px - 8px)`,
    minWidth: "200px",
    [theme.breakpoints.up(DRAWER_BREAKPOINT)]: {
      transition: drawerExpansion.transitionMargin,
    },
    [theme.breakpoints.down(DRAWER_BREAKPOINT)]: {
      transition: drawerCollapse.transitionMargin,
    },
  },
  dropdownContainerOpen: {
    transition: drawerExpansion.transitionMargin,
  },
  dropdownContainerClose: {
    transition: drawerCollapse.transitionMargin,
  },
  dropdownEligible: {
    cursor: "pointer",
    position: "relative",
    top: -8,
  },
  image: {
    width: 30,
    height: 30,
  },
  logoContainer: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.up(DRAWER_BREAKPOINT)]: {
      cursor: "pointer", // onClick is set only on the large screen
    },
    paddingRight: 40,
  },
  logo: {
    width: 100,
  },
  mCMessageBadge: {
    marginRight: 6,
    top: -6,
  },
  ...badgePulseStyles,
  companyBadge: {
    backgroundColor: theme.palette.error.dark,
    color: theme.palette.primary.contrastText,
  },
  menuImgContainer: {
    height: "100%",
    background: theme.palette.primary.main,
    display: "flex",
    paddingLeft: 16,
    paddingRight: 16,
    width: 78,
    justifyContent: "center",
    [theme.breakpoints.up(DRAWER_BREAKPOINT)]: {
      cursor: "pointer", // onClick is set only on the large screen
    },
  },
  menuImgContainerOpen: {
    transition: drawerExpansion.transitionWidth,
  },
  menuImgContainerClose: {
    transition: drawerCollapse.transitionWidth,
  },
  menuImg: {
    height: 64,
    fontSize: "2rem",
    transform: "rotate(0deg)",
    transition: "transform 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
  },
  notificationButton: {
    color: "#C3CCDD",
    fontSize: theme.typography.pxToRem(30),
    "&:hover": {
      backgroundColor: "#F2F2F2",
    },
    marginLeft: "6px",
  },
  rotateIcon: {
    transform: "rotate(180deg)",
  },
  title: {
    flexGrow: 1,
    color: "#C7D5DE",
    cursor: "pointer",
    fontSize: theme.typography.pxToRem(24),
    fontStyle: "italic",
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: 1,
    paddingLeft: "15px",
    "& > svg": {
      position: "relative",
      top: 4,
      left: 8,
    },
  },
  toolBar: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    padding: 0,
    paddingLeft: 0,
  },
}));

const AppBarFull = (props: AppbarPropsType) => {
  const {
    badgeCounts,
    isBrowserDrawerExpanded,
    onClickAvatar,
    onLogoutUser,
    onMouseEnteredDropdown,
    onMouseLeftAvatar,
    onMouseLeftDropdown,
    onToggleBrowserDrawer,
    showSettingsDropdown,
  } = props;
  const navigate = useNavigate();

  const { user } = useContext(UserContext);
  const { classes, cx } = useStyles();
  const [rotateIcon, setRotateIcon] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const { currentCompanyUnreadCount, otherCompaniesUnreadCount } =
    getCompanyUnreadCounts(badgeCounts, user.company.id);
  const pulseCurrentCompany = useBadgePulse(currentCompanyUnreadCount);
  const pulseOtherCompanies = useBadgePulse(otherCompaniesUnreadCount);

  const rotateToggleBrowserDrawer = () => {
    setRotateIcon(!rotateIcon);
    onToggleBrowserDrawer();
  };

  const handleShowCompanyDropdown = () => {
    setShowCompanyDropdown(!showCompanyDropdown);
  };

  const onSwitchCompany = (companyId: number) => {
    setShowCompanyDropdown(false);
    handleSwitchCompany(companyId, navigate);
  };

  return (
    <MuiAppBar position="fixed" className={classes.appBar}>
      <Toolbar id="drawer-toggle-button" className={classes.toolBar}>
        <div className={classes.appBarMargin}>
          <div
            className={classNames(classes.menuImgContainer, {
              [classes.menuImgContainerOpen]: isBrowserDrawerExpanded,
              [classes.menuImgContainerClose]: !isBrowserDrawerExpanded,
            })}
            onClick={rotateToggleBrowserDrawer}
          >
            <Menu
              className={classNames(classes.menuImg, {
                [classes.rotateIcon]: rotateIcon,
              })}
              aria-label={"Toggle menu"}
            />
          </div>
        </div>
        <Link id="app-bar-swept-logo" className={classes.logoContainer} to="/">
          <img className={classes.logo} src={sweptLogo} alt="Swept" />
        </Link>
        <Grid container flexDirection="column">
          <Grid item>
            <Typography
              id="app-bar-company-name"
              className={classes.title}
              color="inherit"
              variant="h6"
              onClick={handleShowCompanyDropdown}
              title={
                otherCompaniesUnreadCount > 0
                  ? `${otherCompaniesUnreadCount} unread in other companies`
                  : undefined
              }
            >
              {user.companies.length > 1 && otherCompaniesUnreadCount > 0 && (
                <Badge
                  className={classes.mCMessageBadge}
                  badgeContent={otherCompaniesUnreadCount}
                  max={99}
                  classes={{
                    badge: cx(
                      classes.companyBadge,
                      pulseOtherCompanies && classes.badgePulse
                    ),
                  }}
                  data-testid="app-bar-multiple-company-messages-badge"
                  aria-label={`${otherCompaniesUnreadCount} unread in other companies`}
                ></Badge>
              )}
              {user.company.name}
              {user.companies?.length > 1 && <ArrowDropDown />}
            </Typography>
          </Grid>
          {user.companies.length > 1 && (
            <Grid item>
              <Collapse
                in={showCompanyDropdown}
                timeout="auto"
                className={classNames(classes.dropdown, {
                  [classes.dropdownContainerOpen]: isBrowserDrawerExpanded,
                  [classes.dropdownContainerClose]: !isBrowserDrawerExpanded,
                })}
                onMouseLeave={() => handleShowCompanyDropdown()}
              >
                <List component="div" disablePadding>
                  {user.companies
                    .filter(
                      (company: CompanyType) => company.id !== user.company.id
                    )
                    .map((oneCompany: CompanyType) => {
                      return (
                        <ListItem
                          id={oneCompany.id.toString()}
                          button
                          key={oneCompany.id}
                          onClick={() => onSwitchCompany(oneCompany.id)}
                        >
                          <ListItemText primary={oneCompany.name} />
                          <Badge
                            badgeContent={badgeCounts[oneCompany.id] || 0}
                            max={99}
                            classes={{ badge: classes.companyBadge }}
                            title={`${
                              badgeCounts[oneCompany.id] || 0
                            } unread in ${oneCompany.name}`}
                            aria-label={`${
                              badgeCounts[oneCompany.id] || 0
                            } unread in ${oneCompany.name}`}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              </Collapse>
            </Grid>
          )}
        </Grid>

        <IconButton
          id="app-bar-messages"
          data-testid="app-bar-messages"
          className={classes.notificationButton}
          color="inherit"
          onClick={() => navigate("/messaging")}
        >
          <Badge
            id="badge-count-messages"
            badgeContent={currentCompanyUnreadCount}
            max={99}
            classes={{
              badge: cx(
                classes.badge,
                pulseCurrentCompany && classes.badgePulse
              ),
            }}
          >
            <Forum fontSize="inherit" />
          </Badge>
        </IconButton>
        <IconButton
          id="app-bar-avatar"
          onMouseLeave={onMouseLeftAvatar}
          onClick={onClickAvatar}
        >
          <Avatar
            name={user.firstName.split(" ")[0] + " " + user.lastName}
            size="36"
            round
            textSizeRatio={1.7}
            color="#009eef"
            maxInitials={2}
          />
        </IconButton>
      </Toolbar>
      <AppBarDropdown
        onMouseLeftDropdown={onMouseLeftDropdown}
        onMouseEnteredDropdown={onMouseEnteredDropdown}
        showSettingsDropdown={showSettingsDropdown}
        onLogoutUser={onLogoutUser}
      />
    </MuiAppBar>
  );
};

export default AppBarFull;
